import { Button, Card, Descriptions, Link, Message, Modal, Popconfirm, Space, Tabs, Table, Tag } from "@arco-design/web-react"
import { InvoiceAPI, CustomerAPI } from "@/apis/sales";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Number, Currency } from "@/components/Numeric";
import { FileUtil } from "@/utils";
import { InvoiceStatusDef, InvoiceStatus } from "@/enums/sales";
import { GLImpactType, TermsDef } from "@/enums/common";
import { EnumLabel } from "@/components/Enum";
import GLImpactButton from "@/components/GLImpactButton";
import SaleOrderLink from "@/components/SaleOrderLink";
import SendEmailButton from "@/components/SendEmailButton";
import Reference from "@/components/Reference";

const TabPane = Tabs.TabPane;

const PaymentLink = ({ invoiceId, children }) => {
    const [visible, setVisible] = useState(false);
    const [data, setData] = useState();

    const getPayments = async () => {
        const values = await InvoiceAPI.payments(invoiceId);
        setData(values);
    }

    return (
        <>
            <Link onClick={() => setVisible(true)}>{children}</Link>
            <Modal visible={visible}
                style={{ width: 650 }}
                afterOpen={() => getPayments()}
                onOk={() => setVisible(false)}
                onCancel={() => setVisible(false)}
                title="Payments"
            >
                <Table rowKey="id" columns={[{
                    title: "Account Name",
                    dataIndex: "accountName"
                }, {
                    title: "Payment Date",
                    dataIndex: "date"
                }, {
                    title: "Payment Amount",
                    render: (col, record) => <Currency value={record.amount} />
                }]}
                    data={data} />
            </Modal>
        </>
    )
}

export const InvoiceInfo = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState({});
    const [hasPayment, setHasPayment] = useState(false);
    const [printLoading, setPrintLoading] = useState(false);

    const getInfo = useCallback(async () => {
        const values = await InvoiceAPI.get(params.invoiceId);
        setInvoice(values);
        setHasPayment(values.paidAmount > 0);
    }, [params]);

    const back = () => {
        navigate("/workspace/sales/invoice");
    }

    const voidInvoice = async () => {
        await InvoiceAPI.voidInvoice(params.invoiceId);
        Message.success("Invoice voided successfully");
        getInfo();
    }

    useEffect(() => {
        getInfo();
    }, [getInfo]);

    return (
        <Card title={
            <Space>
                <span>Invoice #{invoice.invoiceNo}</span>
                <Tag><EnumLabel enumObj={InvoiceStatusDef} value={invoice.status} /></Tag>
            </Space>
        }
            extra={
                <Space>
                    {[InvoiceStatus.Current, InvoiceStatus.Partial, InvoiceStatus.PastDue, InvoiceStatus.PastDuePartial].includes(invoice.status) &&
                        <Button type="primary" onClick={() => navigate(`/workspace/sales/invoice/${params.invoiceId}/edit`)}>Edit</Button>
                    }
                    {invoice.status !== InvoiceStatus.Voided && !hasPayment &&
                        <Popconfirm title="Are you sure you want to void invoice?"
                            onOk={() => voidInvoice()}>
                            <Button type="primary" status="danger">Void Invoice</Button>
                        </Popconfirm>
                    }
                    {invoice.status !== InvoiceStatus.Voided &&
                        <GLImpactButton type={GLImpactType.Invoice} from={params.invoiceId} />
                    }
                    {invoice.status !== InvoiceStatus.Voided &&
                        <Button type="primary" onClick={async () => {
                            setPrintLoading(true);
                            try {
                                const res = await InvoiceAPI.print(invoice.id);
                                FileUtil.download(res.data, `${invoice.invoiceNo}.pdf`)
                            } catch (error) {
                                Message.error(error.message);
                            } finally {
                                setPrintLoading(false);
                            }
                        }} loading={printLoading}>Print</Button>
                    }
                    {invoice.status !== InvoiceStatus.Voided &&
                        <SendEmailButton getEmailContent={async () => {
                            return await InvoiceAPI.getEmail(invoice.id);
                        }} getToList={async () => {
                            const customer = await CustomerAPI.get(invoice.customerId);
                            return customer.contacts;
                        }} sendEmail={async (val) => {
                            await InvoiceAPI.sendEmail(invoice.id, val);
                        }} />
                    }
                    <Button onClick={() => back()}>Back</Button>
                </Space >
            }>
            <Descriptions colon=":" layout="inline-horizontal" data={[
                { label: "Customer", value: invoice.customerName },
                { label: "Invoice Date", value: invoice.invoiceDate },
                { label: "Address", value: invoice.address },
                { label: "SO#", value: <SaleOrderLink soId={invoice.soId}>{invoice.soNo}</SaleOrderLink> },
                { label: "Reference#", value: <Reference value={invoice.reference} /> },
                { label: "Terms", value: <EnumLabel enumObj={TermsDef} value={invoice.term} /> },
                { label: "Due Date", value: invoice.dueDate },
                { label: "Total Amount", value: <Currency value={invoice.totalAmount} /> },
                {
                    label: "Payment Amount",
                    value: <PaymentLink invoiceId={invoice.id}>
                        <Currency value={invoice.paidAmount} />
                    </PaymentLink>
                },
                { label: "Balance Due", value: <Currency value={invoice.totalAmount - invoice.paidAmount} /> },
                { label: "Memo", value: invoice.memo, span: 2 },
            ]} />

            <Tabs defaultActiveTab="1" type="card-gutter">
                <TabPane key="1" title={
                    <Space size={80}>
                        <span>Items:</span>
                        <span><Currency value={invoice.totalItem} /></span>
                    </Space>
                }>
                    <Table rowKey="id" columns={[
                        { title: "Item", dataIndex: "itemName" },
                        { title: "Description", dataIndex: "description" },
                        { title: "Qty", render: (col, record) => <Number value={record.quantity} /> },
                        { title: "U/M", dataIndex: "uom" },
                        { title: "Price", render: (col, record) => <Currency value={record.cost} /> },
                        { title: "Amount", render: (col, record) => <Currency value={record.amount} /> },

                    ]} data={invoice.items} />

                </TabPane>
                <TabPane key="2" title={
                    <Space size={80}>
                        <span>Revenue:</span>
                        <span><Currency value={invoice.totalRevenue} /></span>
                    </Space>
                }>
                    <Table rowKey="id" columns={[
                        { title: "Account", dataIndex: "accountName" },
                        { title: "Amount", render: (col, record) => <Currency value={record.amount} /> },
                        { title: "Memo", dataIndex: "memo" },

                    ]} data={invoice.revenues} />
                </TabPane>
            </Tabs>
        </Card >
    )
}
